/**
 * RBAC v2 (ADR 0048, R-PL-4): role bindings at a scope, roles as named sets of capabilities from
 * the one permission catalogue, identity-provider groups, environment approval policies,
 * separation of duties, break-glass and service accounts.
 *
 * The contract types come first, then the engine: the permission catalogue (`PERMISSIONS`, the
 * AI capabilities plus `ADMIN_CAPABILITIES`), the built-in role sets, `bindingsFromV1`, the pure
 * decision (`authorize`, `authorizeCapability`, `explainAccess`), separation of duties, quorum
 * and break-glass. The route table is `permissionFor` in ./rbac-routes.ts. The hub, the control
 * plane and the console decide and explain with the same functions. Until a module moves to
 * `ctx.authorize`, `Actor.role` keeps its v1 meaning (`orgRoleOf`) and scoped grants do nothing
 * there. Bindings only add: there are no deny bindings. Browser-safe.
 */
import type { ActionKind, Id, Ts } from '../index.ts';
import { AGENT_DEFAULT_CEILING, AI_CAPABILITIES, ROLE_CEILING, aiScopesOf, isCapabilityTier, maxTier, tierRank } from './ai.ts';
import type { AiIntent, Capability, CapabilityKind, CapabilityTier } from './ai.ts';
import { ROLE_RANK } from './auth.ts';
import type { AccessRule, Member, Role } from './auth.ts';
import { actionRoleFor } from './containment.ts';
import { ulid } from './ids.ts';
import { permissionFor } from './rbac-routes.ts';
import type { RbacPlane, RouteRequest } from './rbac-routes.ts';

// ---------------------------------------------------------------------------
// Scopes and resources
// ---------------------------------------------------------------------------

export type RbacScopeKind = 'org' | 'workspace' | 'project' | 'environment' | 'machine';
/** Widest first. A machine scope stands beside the tree: machines are org-wide unless shared. */
export const RBAC_SCOPE_KINDS: readonly RbacScopeKind[] = ['org', 'workspace', 'project', 'environment', 'machine'];

/** Where a binding holds. Org contains workspaces, a workspace its projects, a project its environments. */
export type RbacScope =
  | { kind: 'org' }
  | { kind: 'workspace'; id: Id }
  | { kind: 'project'; id: Id }
  | { kind: 'environment'; projectId: Id; env: string }
  | { kind: 'machine'; id: Id };

/**
 * What a request acts on; the same shapes as a scope (an org-only capability names { kind: 'org' }).
 * A machine resource may also name the project it works in (a session in a checkout), so a
 * project or workspace binding reaches it as well as a binding on that machine.
 */
export type RbacResource = Exclude<RbacScope, { kind: 'machine' }> | { kind: 'machine'; id: Id; projectId?: Id };

/** How a catalogue entry names its object: an org-only capability, or one of the scoped kinds. */
export type RbacResourceKind = RbacScopeKind;

/**
 * What RBAC adds to each catalogue entry (AI capabilities and admin permissions alike): where it
 * can be granted, and what kind of object a request names. Tier stays the agent safety class.
 */
export interface PermissionMeta {
  bindable: RbacScopeKind[];
  resource: RbacResourceKind;
}

// ---------------------------------------------------------------------------
// Subjects, roles, bindings
// ---------------------------------------------------------------------------

export type RbacSubjectKind = 'member' | 'team' | 'group' | 'service-account';

export interface RbacSubject {
  kind: RbacSubjectKind;
  /** mem_..., a team id, '<provider>:<group>' for a group, svc_... */
  id: string;
}

/** The v1 roles, kept with their meaning, plus the new built-in roles for common jobs. */
export type BuiltInRoleName =
  | 'owner'
  | 'admin'
  | 'operator'
  | 'viewer'
  | 'approver'
  | 'deployer'
  | 'auditor'
  | 'billing'
  | 'machine-owner'
  | 'standards-maintainer'
  | 'design-owner';

export const BUILT_IN_ROLE_NAMES: readonly BuiltInRoleName[] = [
  'owner',
  'admin',
  'operator',
  'viewer',
  'approver',
  'deployer',
  'auditor',
  'billing',
  'machine-owner',
  'standards-maintainer',
  'design-owner',
];

export const BUILT_IN_ROLE_TITLE: Record<BuiltInRoleName, string> = {
  owner: 'Owner',
  admin: 'Admin',
  operator: 'Operator',
  viewer: 'Viewer',
  approver: 'Approver',
  deployer: 'Deployer',
  auditor: 'Auditor',
  billing: 'Billing',
  'machine-owner': 'Machine owner',
  'standards-maintainer': 'Standards maintainer',
  'design-owner': 'Design owner',
};

/** A role: a named set of capabilities. Built-in roles carry their name as id; custom roles rol_<ulid>. */
export interface RbacRole {
  id: string;
  title: string;
  description: string;
  /** Capability and permission names; a tier name ('read') stands for every capability of that tier. */
  capabilities: string[];
  builtIn: boolean;
  /** The highest tier among its capabilities, shown beside the role. */
  highestTier: CapabilityTier;
  /** Custom roles are versioned like policy. */
  version: number;
  updatedBy: string;
  updatedAt: Ts;
}

export type RoleBindingSource = 'migration' | 'manual' | 'invite' | 'idp' | 'scim' | 'breakglass' | 'setup' | 'pairing';

export interface RoleBinding {
  /** rbn_<ulid> */
  id: Id;
  subject: RbacSubject;
  /** A built-in role name or rol_<ulid>. */
  role: string;
  scope: RbacScope;
  source: RoleBindingSource;
  grantedBy: string;
  grantedAt: Ts;
  expiresAt?: Ts;
  reason?: string;
}

export function newRoleBindingId(now?: number): Id {
  return `rbn_${ulid(now)}`;
}

export function newRoleId(now?: number): Id {
  return `rol_${ulid(now)}`;
}

// ---------------------------------------------------------------------------
// Identity-provider groups
// ---------------------------------------------------------------------------

export type GroupProvider = 'github' | 'google' | 'microsoft';

/** Members of a group get a role at a scope, reconciled at every sign-in. A group never admits anyone. */
export interface GroupMapping {
  /** grm_<ulid> */
  id: Id;
  provider: GroupProvider;
  /** GitHub 'org' or 'org/team-slug'; a Google group email; an Entra group object id. */
  group: string;
  /** Never 'owner'. Admin-level mappings need an owner to create them. */
  role: string;
  scope: RbacScope;
  createdBy: string;
  createdAt: Ts;
}

export function newGroupMappingId(now?: number): Id {
  return `grm_${ulid(now)}`;
}

// ---------------------------------------------------------------------------
// Environment approval policies and quorum
// ---------------------------------------------------------------------------

export type ApprovalPolicyAppliesTo = 'deploy' | 'promote' | 'rollback' | 'landing' | 'ci.gate' | 'secrets.lease' | 'tool';

/** Narrowest scope wins: environment over project over org. */
export type ApprovalPolicyScope = { kind: 'org' } | { kind: 'project'; id: Id } | { kind: 'environment'; projectId: Id; env: string };

export interface ApprovalPolicy {
  /** apl_<ulid> */
  id: Id;
  scope: ApprovalPolicyScope;
  appliesTo: ApprovalPolicyAppliesTo[];
  /** 1 to 5. */
  minApprovers: number;
  /** Raise the count when enough people are eligible (default: two from three eligible). */
  raiseTo?: { approvers: number; whenEligibleAtLeast: number };
  /** Default: anyone holding environment.approve at that scope. */
  eligible: 'capability' | { teams: string[] };
  /** The requester cannot approve. */
  forbidSelfApproval: boolean;
  /** Whoever submitted the landing, owns the mission, launched the session or pushed the commits cannot approve. */
  forbidAuthorApproval: boolean;
  /** Approvers from at least two teams. */
  distinctTeams?: boolean;
  /** Then the R-HL-5 expiry policy applies. */
  expiresAfterMinutes: number;
  updatedBy: string;
  updatedAt: Ts;
}

export function newApprovalPolicyId(now?: number): Id {
  return `apl_${ulid(now)}`;
}

export const APPROVAL_POLICY_MAX_APPROVERS = 5;

/**
 * The proposed default for production (ADR 0048 point 8, owner to confirm; a setting, not code):
 * one approver who is neither the requester nor the author, two when three or more are eligible.
 */
export const DEFAULT_PRODUCTION_APPROVAL_POLICY: Readonly<Pick<ApprovalPolicy, 'appliesTo' | 'minApprovers' | 'raiseTo' | 'eligible' | 'forbidSelfApproval' | 'forbidAuthorApproval' | 'expiresAfterMinutes'>> = {
  appliesTo: ['deploy', 'promote', 'rollback', 'ci.gate'],
  minApprovers: 1,
  raiseTo: { approvers: 2, whenEligibleAtLeast: 3 },
  eligible: 'capability',
  forbidSelfApproval: true,
  forbidAuthorApproval: true,
  expiresAfterMinutes: 24 * 60,
};

// ---------------------------------------------------------------------------
// Separation of duties
// ---------------------------------------------------------------------------

export type SodRuleId = 'sod/self-approval' | 'sod/self-grant' | 'sod/two-person-admin' | 'sod/policy-and-approve' | 'sod/breakglass';

/** Checked after grants; each refusal and audit entry names its rule. */
export const SOD_RULES: Readonly<Record<SodRuleId, string>> = {
  'sod/self-approval': 'A person cannot approve a request they raised, or a change they authored, where the policy says so.',
  'sod/self-grant': 'A person cannot create or widen a binding, role, mapping or token for themselves.',
  'sod/two-person-admin': 'Granting admin or owner at org scope, creating an admin-level mapping, or loosening a production approval policy needs a second admin once the org has two.',
  'sod/policy-and-approve': 'Whoever changed an environment approval policy cannot approve under it for 24 hours.',
  'sod/breakglass': 'A break-glass activation is reviewed by someone other than the person who used it.',
};

/** How long sod/policy-and-approve holds. */
export const SOD_POLICY_COOLDOWN_MS = 24 * 60 * 60_000;

// ---------------------------------------------------------------------------
// Break-glass
// ---------------------------------------------------------------------------

export interface BreakGlass {
  /** brk_<ulid> */
  id: Id;
  by: string;
  role: string;
  scope: RbacScope;
  reason: string;
  startedAt: Ts;
  expiresAt: Ts;
  endedAt?: Ts;
  endedBy?: string;
  /** sod/breakglass: someone other than `by`. */
  reviewedBy?: string;
  reviewedAt?: Ts;
  reviewNote?: string;
}

export function newBreakGlassId(now?: number): Id {
  return `brk_${ulid(now)}`;
}

/** Defaults the owner confirms (settings, not code). */
export const BREAKGLASS_DEFAULT_MINUTES = 60;
export const BREAKGLASS_MAX_MINUTES = 4 * 60;
/** Activation needs a sign-in this recent. */
export const BREAKGLASS_RECENT_SIGNIN_MS = 10 * 60_000;

// ---------------------------------------------------------------------------
// Service accounts and tokens v2
// ---------------------------------------------------------------------------

/** A subject with no sign-in: tokens are its only credential. Never wider than its owner could grant. */
export interface ServiceAccount {
  /** svc_<ulid> */
  id: Id;
  name: string;
  description: string;
  owner: { kind: 'member' | 'team'; id: string };
  /** Agent service accounts can never reach critical. */
  agent: boolean;
  createdBy: string;
  createdAt: Ts;
  disabledAt?: Ts;
}

export function newServiceAccountId(now?: number): Id {
  return `svc_${ulid(now)}`;
}

/** What a v2 token adds: its subject and the resources it is narrowed to. */
export interface TokenGrantV2 {
  subject: RbacSubject;
  /** Capability names or tiers, as today. */
  scopes: string[];
  /** Narrowed to these projects or environments; absent: everything the subject holds. */
  resources?: RbacResource[];
  /**
   * A token minted before RBAC v2. It keeps its own v1 role, and its scopes keep their v1 meaning
   * (read, write, admin, actions, action kinds; tiers and capability names only at the AI gate)
   * until it expires, so no token changes meaning in place.
   */
  legacy?: { role: Role };
}

// ---------------------------------------------------------------------------
// The decision and its explanation
// ---------------------------------------------------------------------------

/** Who is asking, as the decision sees them. Linked sign-ins of one person share a member id. */
export interface RbacCaller {
  kind: 'member' | 'service-account' | 'agent';
  id: string;
  /** The member an agent acts for (the assistant runs with the asking person's grants). */
  onBehalfOf?: string;
  teams?: string[];
  groups?: { provider: GroupProvider; group: string }[];
  token?: TokenGrantV2;
}

/** Everything `authorize` reads: a pure snapshot, cached by `rev`. */
export interface RbacSnapshot {
  rev: number;
  roles: RbacRole[];
  bindings: RoleBinding[];
  groupMappings: GroupMapping[];
  approvalPolicies: ApprovalPolicy[];
  breakglass: BreakGlass[];
  serviceAccounts: ServiceAccount[];
  /** Which projects each workspace contains (null: all). */
  workspaces: { id: Id; projectIds: Id[] | null }[];
  /** False when the org turned break-glass off (ADR 0048 point 10). Absent: on. */
  breakglassEnabled?: boolean;
}

export interface AuthorizeDecision {
  allowed: boolean;
  /** The bindings that granted it (empty when refused). */
  via: Id[];
  /** One sentence: why it was allowed or refused. */
  why: string;
  /** Set when a separation-of-duties rule refused it. */
  sod?: SodRuleId;
  /** Set when an active break-glass granted it. */
  breakglassId?: Id;
}

/** POST /api/rbac/explain */
export interface AccessExplainRequest {
  subject: RbacSubject;
  capability: string;
  resource: RbacResource;
}

/** The same reasoning as `authorize`, as sentences a person can follow and click through. */
export interface AccessExplanation {
  allowed: boolean;
  lines: string[];
  bindings: RoleBinding[];
  policy?: ApprovalPolicy;
  sod?: SodRuleId;
}

/** How a change was allowed, carried inside its audit entry (the hash chain is unchanged). */
export interface AuthzRecord {
  capability: string;
  resource: RbacResource;
  via: { bindings: Id[] } | { breakglass: Id } | { intent: string } | { plan: { id: Id; step: string } };
  approvals?: { by: string; at: Ts }[];
}

// ---------------------------------------------------------------------------
// Access review and state
// ---------------------------------------------------------------------------

/** GET /api/rbac/review: one row per subject, binding and scope. */
export interface AccessReviewRow {
  subject: RbacSubject;
  subjectName: string;
  role: string;
  scope: RbacScope;
  bindingId: Id;
  source: RoleBindingSource;
  lastUsedAt: Ts | null;
  /** Unused for 90 days. */
  stale: boolean;
}

export const ACCESS_REVIEW_STALE_DAYS = 90;

/** state.ext.rbac and GET /api/rbac: the snapshot as the console shows it. */
export interface RbacState extends RbacSnapshot {
  /** Hub modules that honour scoped grants yet; elsewhere only org roles count. */
  scopedModules: string[];
  /** True while nothing is written: v1 roles synthesised in memory. */
  synthesised: boolean;
}

/** POST /api/rbac/bindings */
export interface RoleBindingRequest {
  subject: RbacSubject;
  role: string;
  scope: RbacScope;
  expiresAt?: Ts;
  reason?: string;
}

/** POST /api/rbac/roles, PATCH /api/rbac/roles/:id */
export interface RoleRequest {
  title: string;
  description?: string;
  capabilities: string[];
}

/** POST /api/rbac/policies, PATCH /api/rbac/policies/:id */
export type ApprovalPolicyRequest = Omit<ApprovalPolicy, 'id' | 'updatedBy' | 'updatedAt'>;

/** POST /api/rbac/breakglass */
export interface BreakGlassRequest {
  role: string;
  scope: RbacScope;
  reason: string;
  minutes?: number;
}

// ===========================================================================
// The engine (RBAC-1): pure functions over a snapshot, shared by the hub, the control plane
// and the console.
// ===========================================================================

export { permissionFor, resourceOfPath } from './rbac-routes.ts';
export type { RbacPlane, RouteRequest, RoutePermission } from './rbac-routes.ts';

// ---------------------------------------------------------------------------
// The permission catalogue (ADR 0048 point 1)
// ---------------------------------------------------------------------------

/**
 * The v1 token scope a permission needs where it is used directly (a route, an action):
 * 'read' for reads, 'write' for other changes, 'admin' for the admin paths, 'actions' for an
 * action (or the action kind itself), null when the route gates each call itself (POST
 * /api/actions, POST /api/ai/invoke).
 */
export type PermissionTokenScope = 'read' | 'write' | 'admin' | 'actions' | null;

/** A permission that is not (yet) an AI capability: administrative, self-service, route-level. */
export interface AdminCapability extends PermissionMeta {
  name: string;
  title: string;
  description: string;
  /** The agent safety class, as in the AI catalogue. Not the permission. */
  tier: CapabilityTier;
  tokenScope: PermissionTokenScope;
  /** The lowest v1 role that holds it: owner, admin, operator and viewer are built from this. */
  v1Role: Role;
  /** A setup step authorised as this permission (role sets name the permission, not the step). */
  actsAs?: string;
}

/** One entry of the joined catalogue: `PERMISSIONS = [...AI_CAPABILITIES, ...ADMIN_CAPABILITIES]`. */
export interface Permission extends AdminCapability {
  source: 'ai' | 'admin';
  /** For AI capabilities: where it is carried out (the AI gate's broad scopes read it). */
  kind?: CapabilityKind;
  /** For action capabilities: the action kind. */
  action?: ActionKind;
}

const ALL_SCOPES: RbacScopeKind[] = ['org', 'workspace', 'project', 'environment', 'machine'];
const ORG_ONLY: RbacScopeKind[] = ['org'];
const PROJECTS: RbacScopeKind[] = ['org', 'workspace', 'project'];
const ENVIRONMENTS: RbacScopeKind[] = ['org', 'workspace', 'project', 'environment'];
const MACHINES: RbacScopeKind[] = ['org', 'workspace', 'project', 'machine'];
const WORKSPACES: RbacScopeKind[] = ['org', 'workspace'];

const cap = (
  name: string,
  title: string,
  description: string,
  tier: CapabilityTier,
  v1Role: Role,
  tokenScope: PermissionTokenScope,
  bindable: RbacScopeKind[],
  resource: RbacResourceKind,
  actsAs?: string,
): AdminCapability => ({ name, title, description, tier, v1Role, tokenScope, bindable, resource, ...(actsAs ? { actsAs } : {}) });

/**
 * Administrative and route-level permissions that hide behind `api.call` path prefixes or the v1
 * route tables today, plus the CI, setup and standards permissions of ADRs 0046, 0047 and 0050.
 * `v1Role` is what v1 grants: for a route permission, what the v1 route table asks; for a module
 * permission, what the module checks today. The equivalence test proves the route ones.
 */
export const ADMIN_CAPABILITIES: readonly AdminCapability[] = [
  // Route-level: what the v1 route tables let every role, or every operator, do.
  cap('ai.invoke', 'Use the AI API', 'Call POST /api/ai/invoke; each capability is then gated by itself.', 'read', 'viewer', null, ORG_ONLY, 'org'),
  cap('actions.run', 'Run actions', 'Call POST /api/actions; each action then needs its own capability.', 'operate', 'operator', null, MACHINES, 'machine'),
  cap('logs.stream', 'Stream logs', 'Open or close a live log stream.', 'read', 'viewer', 'read', ['org', 'machine'], 'machine'),
  cap('presence.beat', 'Show presence', 'Tell others what you are looking at.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('teams.route.preview', 'Preview routing', 'Preview who a Needs-you item would go to.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('fleet.inspect', 'Inspect the fleet', 'Export the machine inventory, or check machine health or agent versions; each is audited.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('policy.simulate', 'Simulate policy', 'Evaluate or simulate a policy without changing it.', 'read', 'viewer', 'read', PROJECTS, 'project'),
  cap('self.manage', 'Manage your own account', 'Your notification preferences and devices, your linked sign-ins, your sessions and your own tokens.', 'operate', 'viewer', 'write', ORG_ONLY, 'org'),
  cap('self.session', 'Sign in and out', 'Start or end your own browser session.', 'read', 'viewer', null, ORG_ONLY, 'org'),
  // People and access.
  cap('members.read', 'See members and their access', "See every member, their sign-ins and grants, and explain another person's access.", 'read', 'admin', 'read', WORKSPACES, 'org'),
  cap('members.invite', 'Invite people', 'Invites, GitHub access rules and domain or tenant join rules.', 'critical', 'admin', 'admin', WORKSPACES, 'workspace'),
  cap('members.remove', 'Remove members', 'Remove a member and end their sessions.', 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('members.manage', "Manage members' sign-ins", "End a member's sessions everywhere and unlink their sign-ins.", 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('members.owner', 'Grant the owner role', 'Grant, change or remove the owner role, and allow a removed owner back.', 'critical', 'owner', 'admin', ORG_ONLY, 'org'),
  cap('roles.manage', 'Manage roles', 'Create, change and delete custom roles.', 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('bindings.manage', 'Grant roles', "Bind a role to a member, team, group or service account at a scope, including a member's role at the org.", 'critical', 'admin', 'admin', ALL_SCOPES, 'workspace'),
  cap('tokens.manage', 'Manage API tokens', "Create tokens and revoke anyone's.", 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('service-accounts.manage', 'Manage service accounts', 'Create, disable and issue tokens for service accounts.', 'critical', 'admin', 'admin', PROJECTS, 'project'),
  cap('teams.manage', 'Manage the org and teams', 'The org, workspaces, teams, project owners, the rota and routing rules.', 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('auth.configure', 'Configure sign-in', 'Sign-in providers and the sign-in policy.', 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('approval-policies.manage', 'Manage approval policies', 'Environment approval policies: approver counts, who is eligible, self-approval.', 'critical', 'admin', 'admin', ENVIRONMENTS, 'environment'),
  cap('breakglass.use', 'Use break-glass', 'Take a time-boxed role in an emergency, with a reason, notification and review.', 'critical', 'owner', 'admin', ENVIRONMENTS, 'environment'),
  cap('scim.provision', 'Provision through SCIM', 'Create and remove members and groups from an identity provider.', 'critical', 'owner', 'admin', ORG_ONLY, 'org'),
  // Machines and trust.
  cap('machines.enrol', 'Add computers', 'Create join tokens so a computer can join.', 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('machines.manage', 'Manage computers', "Change a computer's settings.", 'critical', 'admin', 'admin', ORG_ONLY, 'machine'),
  cap('machines.remove', 'Remove computers', 'Remove a computer from the org.', 'critical', 'admin', 'admin', ORG_ONLY, 'machine'),
  cap('machines.drain', 'Drain and resume a computer', 'Stop placing new work on a computer, and resume it.', 'operate', 'operator', 'write', ['org', 'machine'], 'machine'),
  cap('terminals.use', 'Use terminals', 'Open an interactive terminal on a computer.', 'critical', 'operator', 'write', MACHINES, 'machine'),
  cap('trust.manage', 'Manage trust settings', 'Trust settings, unfreezing Orgus and resuming paused projects.', 'critical', 'admin', 'admin', ORG_ONLY, 'org'),
  cap('trust.freeze', 'Freeze changes', 'Freeze every change in the org, or pause one project.', 'operate', 'operator', 'write', PROJECTS, 'project'),
  cap('fleet.rollout', 'Roll out agent updates', 'Update agents, promote rollouts and change update rings.', 'critical', 'admin', 'write', ORG_ONLY, 'org'),
  // Policy, secrets, audit.
  cap('policy.edit', 'Change policy', 'Policy rules, presets, settings and freezes.', 'critical', 'admin', 'admin', PROJECTS, 'project'),
  cap('secrets.manage', 'Manage secrets', 'Add, change, rotate and remove secrets.', 'critical', 'admin', 'write', PROJECTS, 'project'),
  cap('secrets.lease', 'Lease secrets', 'Lease a secret into a session.', 'elevated', 'operator', 'write', ENVIRONMENTS, 'project'),
  cap('audit.export', 'Export the audit log', 'Download the audit log and the access review.', 'read', 'admin', 'read', ORG_ONLY, 'org'),
  cap('audit.configure', 'Configure the audit log', 'Audit retention and security detection rules.', 'critical', 'admin', 'write', ORG_ONLY, 'org'),
  // Delivery: landing, environments, approvals, budgets.
  cap('approval.allow-project', 'Allow for a whole project', 'Answer an approval with an allow that covers the whole project.', 'elevated', 'admin', 'write', PROJECTS, 'project'),
  cap('landing.submit', 'Submit to the landing queue', 'Submit, cancel and retry landings.', 'operate', 'operator', 'write', PROJECTS, 'project'),
  cap('landing.configure', 'Configure landing', 'Verify mode and command, required checks, protected branches.', 'elevated', 'admin', 'write', PROJECTS, 'project'),
  cap('environment.configure', 'Configure environments', 'Environments, deploy modes and freeze windows of protected environments.', 'elevated', 'admin', 'write', ENVIRONMENTS, 'environment'),
  cap('environment.deploy', 'Deploy', 'Deploy, promote and roll back an environment.', 'operate', 'operator', 'write', ENVIRONMENTS, 'environment'),
  cap('environment.protected', 'Change protected environments', 'Deploy to and change a protected environment.', 'elevated', 'admin', 'write', ENVIRONMENTS, 'environment'),
  cap('environment.approve', 'Approve deploys', 'Count toward the approvals a deploy needs. Never an agent or a service account.', 'elevated', 'admin', 'write', ENVIRONMENTS, 'environment'),
  cap('budget.edit', 'Change budgets', 'Org, project and mission budgets.', 'operate', 'operator', 'write', PROJECTS, 'project'),
  cap('finance.read', 'See finance', 'Finance entries, subscriptions and reports.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('finance.edit', 'Change finance', 'Finance entries, receipts, subscriptions and imports.', 'operate', 'operator', 'write', ORG_ONLY, 'org'),
  cap('billing.read', 'See the plan', 'The plan, its limits and invoices.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('billing.manage', 'Manage billing', 'Change the plan and payment details.', 'critical', 'owner', 'admin', ORG_ONLY, 'org'),
  // CI (ADR 0046 point 4).
  cap('ci.runs.list', 'List CI runs', 'CI runs of connected providers.', 'read', 'viewer', 'read', PROJECTS, 'project'),
  cap('ci.run.log', 'Read CI logs', 'The log of a CI run, redacted.', 'read', 'viewer', 'read', PROJECTS, 'project'),
  cap('ci.run.rerun', 'Re-run CI', 'Re-run a CI run or its failed jobs.', 'operate', 'operator', 'write', PROJECTS, 'project'),
  cap('ci.run.dispatch', 'Dispatch CI', 'Start a CI workflow.', 'operate', 'operator', 'write', PROJECTS, 'project'),
  cap('ci.run.cancel', 'Cancel CI', 'Cancel a CI run.', 'operate', 'operator', 'write', PROJECTS, 'project'),
  cap('ci.gate.decide', 'Decide a CI deploy gate', 'Approve or reject a deploy gate a CI run waits on.', 'elevated', 'operator', 'write', ENVIRONMENTS, 'environment'),
  cap('ci.connect', 'Connect CI', 'Connect a CI provider. It handles a credential, so only a person.', 'critical', 'admin', 'admin', PROJECTS, 'project'),
  // Setup and explain (ADR 0047). Setup steps act as the permission they change.
  cap('setup.detect', 'Detect a stack', 'Stack, CI and deploy hints for a repo.', 'read', 'viewer', 'read', PROJECTS, 'project'),
  cap('setup.plan', 'Propose a setup plan', 'A setup plan for a scope; changes nothing.', 'read', 'viewer', 'read', PROJECTS, 'project'),
  cap('member.invite', 'Invite a person (setup)', 'A setup step that invites people or adds a join rule.', 'critical', 'admin', 'admin', WORKSPACES, 'workspace', 'members.invite'),
  cap('role.bind', 'Bind a role (setup)', 'A setup step that binds a role at a scope.', 'critical', 'admin', 'admin', ALL_SCOPES, 'workspace', 'bindings.manage'),
  cap('team.create', 'Create a team (setup)', 'A setup step that creates a team.', 'critical', 'admin', 'admin', ORG_ONLY, 'org', 'teams.manage'),
  cap('approval-policy.set', 'Set an approval policy (setup)', 'A setup step that sets an environment approval policy.', 'critical', 'admin', 'admin', ENVIRONMENTS, 'environment', 'approval-policies.manage'),
  cap('budget.set', 'Set a budget (setup)', 'A setup step that sets a budget.', 'elevated', 'operator', 'write', PROJECTS, 'project', 'budget.edit'),
  cap('policy.preset.apply', 'Apply a policy preset (setup)', 'A setup step that applies a named policy preset.', 'critical', 'admin', 'admin', PROJECTS, 'project', 'policy.edit'),
  cap('notify.channel.add', 'Add a notification channel', 'Add a channel such as Slack or email.', 'elevated', 'admin', 'write', ORG_ONLY, 'org'),
  // Review I4 (18 Oct 2026): the setup step shares the computer with the team, which needs an admin;
  // pairing itself is approved by an admin, into the personal scope of whoever opened it (R-SC-2, ORGU-30).
  cap('machine.pair', 'Add a computer for the team (setup)', 'A setup handoff: the person downloads the client and approves the computer, shared with the team. A computer an operator opens waits for an admin, who approves it into that operator\'s personal scope.', 'critical', 'admin', 'admin', ORG_ONLY, 'org', 'machines.enrol'),
  cap('agents.sign-in', 'Sign in a coding agent (setup)', 'A setup handoff: the person signs a coding agent in on their own computer; Orgus never sees the credential.', 'operate', 'operator', 'write', MACHINES, 'machine'),
  cap('access.explain', 'Explain access', 'Why a person can or cannot do something here.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('policy.explain', 'Explain a policy decision', 'Why a request was allowed, asked or denied.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('pipeline.explain', 'Explain a failure', 'Why a CI run or a landing failed.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('cost.explain', 'Explain spend', 'Where a budget went.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  cap('standards.explain', 'Explain a finding', 'The rule behind a standards finding and how to fix it.', 'read', 'viewer', 'read', ORG_ONLY, 'org'),
  // Standards and design tokens (ADR 0050 point 5).
  cap('standards.read', 'See standards', 'Standards, their layers and findings.', 'read', 'viewer', 'read', PROJECTS, 'project'),
  cap('standards.edit', 'Edit standards', 'Drafts, guidance and project overrides.', 'elevated', 'admin', 'write', PROJECTS, 'project'),
  cap('standards.adopt', 'Adopt standards (setup)', 'A setup step that pins standards packs to a scope.', 'elevated', 'admin', 'write', PROJECTS, 'project', 'standards.edit'),
  cap('standards.publish', 'Publish standards', 'Publish a pack or token set version.', 'elevated', 'admin', 'write', WORKSPACES, 'workspace'),
  cap('standards.enforce', 'Enforce standards', 'An enforced pin that can block every landing in scope.', 'critical', 'admin', 'write', WORKSPACES, 'workspace'),
  cap('standards.pr', 'Open the standards pull request', 'Open the pull request with the repo files.', 'elevated', 'admin', 'write', PROJECTS, 'project'),
  cap('tokens.import', 'Import design tokens', 'Import a token set from a file or Figma.', 'elevated', 'admin', 'write', WORKSPACES, 'workspace'),
  cap('tokens.publish', 'Publish design tokens', 'Publish a token set version.', 'elevated', 'admin', 'write', WORKSPACES, 'workspace'),
  cap('waivers.grant', 'Grant waivers', 'Waive a standards finding for up to 30 days.', 'operate', 'admin', 'write', ['workspace', 'project'], 'project'),
];

const ADMIN_BY_NAME = new Map(ADMIN_CAPABILITIES.map((a) => [a.name, a]));

type PermissionMetaV1 = Pick<AdminCapability, 'bindable' | 'resource' | 'tokenScope' | 'v1Role'>;

/** Where an AI capability can be granted and what it names, until the catalogue carries both itself. */
function aiMeta(c: Capability): PermissionMetaV1 {
  const own = c as Capability & Partial<PermissionMeta>;
  const base = ((): PermissionMetaV1 => {
    if (c.kind === 'read' || (c.kind === 'api' && c.tier === 'read')) return { bindable: ORG_ONLY, resource: 'org', tokenScope: 'read', v1Role: 'viewer' };
    if (c.kind === 'action') {
      const projects = c.action === 'project.add' || c.action === 'project.remove';
      // P0 item 12 (SC-RCE-3): raw job.run needs the admin role, as v1's checkAction says.
      return { bindable: projects ? ['org', 'machine'] : MACHINES, resource: 'machine', tokenScope: 'actions', v1Role: actionRoleFor(c.action ?? '') };
    }
    if (c.kind === 'api') return { bindable: ORG_ONLY, resource: 'org', tokenScope: 'write', v1Role: 'operator' };
    if (c.module === 'approvals') return { bindable: ENVIRONMENTS, resource: 'project', tokenScope: 'write', v1Role: 'operator' };
    if (c.module === 'missions') return { bindable: PROJECTS, resource: 'project', tokenScope: 'write', v1Role: 'operator' };
    // A catalogue entry this table does not know yet: org-only, held from the role its tier implies.
    const v1Role: Role = c.tier === 'read' ? 'viewer' : c.tier === 'operate' ? 'operator' : c.tier === 'elevated' ? 'admin' : 'owner';
    return { bindable: ORG_ONLY, resource: 'org', tokenScope: c.tier === 'read' ? 'read' : 'write', v1Role };
  })();
  return { ...base, ...(own.bindable ? { bindable: own.bindable } : {}), ...(own.resource ? { resource: own.resource } : {}) };
}

function buildPermissions(): Permission[] {
  const out = new Map<string, Permission>();
  for (const c of AI_CAPABILITIES) {
    const admin = ADMIN_BY_NAME.get(c.name);
    const meta = admin ? { bindable: admin.bindable, resource: admin.resource, tokenScope: admin.tokenScope, v1Role: admin.v1Role, ...(admin.actsAs ? { actsAs: admin.actsAs } : {}) } : aiMeta(c);
    out.set(c.name, { name: c.name, title: c.title, description: c.description, tier: c.tier, ...meta, source: 'ai', kind: c.kind, ...(c.action ? { action: c.action } : {}) });
  }
  for (const a of ADMIN_CAPABILITIES) if (!out.has(a.name)) out.set(a.name, { ...a, source: 'admin' });
  return [...out.values()];
}

/** The one permission catalogue: every AI capability, then every admin permission (one entry per name). */
export const PERMISSIONS: readonly Permission[] = buildPermissions();
const PERMISSION_BY_NAME = new Map(PERMISSIONS.map((p) => [p.name, p]));

export function permissionByName(name: string): Permission | undefined {
  return PERMISSION_BY_NAME.get(name);
}

/** A setup step's permission resolves to the permission it acts as; everything else to itself. */
export function canonicalPermission(p: Permission): Permission {
  return (p.actsAs && PERMISSION_BY_NAME.get(p.actsAs)) || p;
}

// ---------------------------------------------------------------------------
// Built-in roles (ADR 0048 point 2)
// ---------------------------------------------------------------------------

const V1_ROLES_HIGH_FIRST: readonly Role[] = ['owner', 'admin', 'operator', 'viewer'];

/** Every permission (not a setup step) that v1 grants to this role. */
function v1Set(role: Role): string[] {
  return PERMISSIONS.filter((p) => !p.actsAs && ROLE_RANK[p.v1Role] <= ROLE_RANK[role]).map((p) => p.name);
}

const READS = v1Set('viewer');

const BUILT_IN_DEFS: Record<BuiltInRoleName, { description: string; capabilities: string[]; ceiling?: CapabilityTier }> = {
  owner: { description: 'Everything, including granting the owner role.', capabilities: v1Set('owner'), ceiling: ROLE_CEILING.owner },
  admin: { description: 'Operator, plus members, tokens, machines, policies, project-wide allows and protected environments.', capabilities: v1Set('admin'), ceiling: ROLE_CEILING.admin },
  operator: { description: 'Viewer, plus actions, approvals once and for the session, missions, landings, deploys and budgets.', capabilities: v1Set('operator'), ceiling: ROLE_CEILING.operator },
  viewer: { description: 'Sees everything and changes nothing but their own account.', capabilities: READS, ceiling: ROLE_CEILING.viewer },
  approver: { description: 'Reads, plus answering approvals and approving deploys.', capabilities: [...READS, 'approval.approve', 'approval.deny', 'environment.approve', 'ci.gate.decide'] },
  deployer: { description: 'Reads, plus submitting landings and deploying.', capabilities: [...READS, 'landing.submit', 'environment.deploy'] },
  auditor: { description: 'Reads, plus exporting the audit log and the access review. Changes nothing.', capabilities: [...READS, 'audit.export'], ceiling: 'read' },
  billing: { description: 'Reads, costs and finance, plus changing budgets.', capabilities: [...READS, 'finance.read', 'billing.read', 'budget.edit'] },
  'machine-owner': { description: 'On one computer: start and stop sessions, terminals, drain and resume.', capabilities: ['session.start', 'session.stop', 'job.cancel', 'actions.run', 'terminals.use', 'machines.drain', 'logs.stream'] },
  'standards-maintainer': { description: 'Edits, publishes and opens pull requests for standards.', capabilities: ['standards.read', 'standards.edit', 'standards.publish', 'standards.pr'] },
  'design-owner': { description: 'Imports and publishes design tokens.', capabilities: ['standards.read', 'standards.publish', 'tokens.import', 'tokens.publish'] },
};

/** The highest tier among a list of capability and tier names. */
export function highestTierOf(capabilities: readonly string[]): CapabilityTier {
  let t: CapabilityTier = 'read';
  for (const name of capabilities) {
    if (isCapabilityTier(name)) t = maxTier(t, name);
    else {
      const p = PERMISSION_BY_NAME.get(name);
      if (p) t = maxTier(t, p.tier);
    }
  }
  return t;
}

/**
 * The built-in roles as sets. owner, admin, operator and viewer grant exactly what the v1 tables
 * grant (the equivalence test), and reach the tier `ROLE_CEILING` gives them at the AI gate.
 */
export const BUILT_IN_ROLES: readonly RbacRole[] = BUILT_IN_ROLE_NAMES.map((name) => {
  const d = BUILT_IN_DEFS[name];
  const capabilities = [...new Set(d.capabilities)];
  return { id: name, title: BUILT_IN_ROLE_TITLE[name], description: d.description, capabilities, builtIn: true, highestTier: d.ceiling ?? highestTierOf(capabilities), version: 1, updatedBy: 'orgus', updatedAt: 0 };
});
const BUILT_IN_BY_ID = new Map(BUILT_IN_ROLES.map((r) => [r.id, r]));

export function isBuiltInRoleName(v: unknown): v is BuiltInRoleName {
  return typeof v === 'string' && BUILT_IN_BY_ID.has(v);
}

export function builtInRole(name: BuiltInRoleName): RbacRole {
  return BUILT_IN_BY_ID.get(name)!;
}

/** A role by id: the snapshot's (custom roles), else a built-in one. */
export function roleById(snapshot: Pick<RbacSnapshot, 'roles'>, id: string): RbacRole | undefined {
  return snapshot.roles.find((r) => r.id === id) ?? BUILT_IN_BY_ID.get(id);
}

const ROLE_SETS = new WeakMap<RbacRole, Set<string>>();

/**
 * Permissions only the built-in owner role holds as a whole: granting the owner role itself
 * (`members.owner`) is never in a custom role, and the other owner-level permissions only when an
 * owner names them one by one (`validateRoleRequest`). A tier name never expands to any of them.
 */
export const OWNER_ONLY_PERMISSIONS: readonly string[] = PERMISSIONS.filter((p) => p.v1Role === 'owner' && !p.actsAs).map((p) => p.name);
const OWNER_ONLY = new Set(OWNER_ONLY_PERMISSIONS);

/** Every permission a role holds, tier names expanded to every permission of that tier (owner-only ones excepted). */
export function roleCapabilities(role: RbacRole): ReadonlySet<string> {
  let set = ROLE_SETS.get(role);
  if (!set) {
    set = new Set<string>();
    for (const name of role.capabilities) {
      if (isCapabilityTier(name)) {
        for (const p of PERMISSIONS) if (p.tier === name && !p.actsAs && !OWNER_ONLY.has(p.name)) set.add(p.name);
      } else {
        const p = PERMISSION_BY_NAME.get(name);
        if (p) set.add(canonicalPermission(p).name);
      }
    }
    ROLE_SETS.set(role, set);
  }
  return set;
}

/** Admin-level: admin, owner, or any role that reaches elevated or critical. */
export function isAdminLevelRole(role: RbacRole): boolean {
  return role.id === 'owner' || role.id === 'admin' || tierRank(role.highestTier) >= tierRank('elevated');
}

// ---------------------------------------------------------------------------
// Scopes
// ---------------------------------------------------------------------------

/** The scope a resource sits in (a machine resource without the project it names). */
export function scopeOfResource(r: RbacResource): RbacScope {
  return r.kind === 'machine' ? { kind: 'machine', id: r.id } : r;
}

function workspaceHas(snapshot: Pick<RbacSnapshot, 'workspaces'>, workspaceId: Id, projectId: Id | undefined): boolean {
  if (!projectId) return false;
  const w = snapshot.workspaces.find((x) => x.id === workspaceId);
  if (!w) return false;
  return w.projectIds === null || w.projectIds.includes(projectId);
}

/**
 * Whether a binding at `scope` reaches `resource`. The org contains everything; a workspace its
 * projects (all of them when its list is null) and their environments; a project its
 * environments; an environment itself. A machine scope stands beside the tree: it reaches that
 * machine only. A machine resource that names its project is reached through the project too.
 */
export function scopeContains(snapshot: Pick<RbacSnapshot, 'workspaces'>, scope: RbacScope, resource: RbacResource): boolean {
  if (scope.kind === 'org') return true;
  const projectOf = resource.kind === 'project' ? resource.id : resource.kind === 'environment' ? resource.projectId : resource.kind === 'machine' ? resource.projectId : undefined;
  switch (scope.kind) {
    case 'workspace':
      if (resource.kind === 'workspace') return resource.id === scope.id;
      return workspaceHas(snapshot, scope.id, projectOf);
    case 'project':
      return projectOf !== undefined && projectOf === scope.id;
    case 'environment':
      return resource.kind === 'environment' && resource.projectId === scope.projectId && resource.env === scope.env;
    case 'machine':
      return resource.kind === 'machine' && resource.id === scope.id;
  }
}

/** A scope as a person reads it, with names when the caller has them. */
export function scopeLabel(scope: RbacScope | RbacResource, names: Readonly<Record<string, string>> = {}): string {
  const n = (id: string): string => names[id] ?? id;
  switch (scope.kind) {
    case 'org':
      return 'the org';
    case 'workspace':
      return `workspace ${n(scope.id)}`;
    case 'project':
      return `project ${n(scope.id)}`;
    case 'environment':
      return `${scope.env} of project ${n(scope.projectId)}`;
    case 'machine':
      return `computer ${n(scope.id)}`;
  }
}

/** Whether a value is a well-formed scope (ids present). */
export function isRbacScope(v: unknown): v is RbacScope {
  if (!v || typeof v !== 'object') return false;
  const s = v as Record<string, unknown>;
  const str = (x: unknown): boolean => typeof x === 'string' && x.length > 0 && x.length <= 200;
  switch (s.kind) {
    case 'org':
      return true;
    case 'workspace':
    case 'project':
    case 'machine':
      return str(s.id);
    case 'environment':
      return str(s.projectId) && str(s.env);
    default:
      return false;
  }
}

/** Whether two scopes are the same scope. */
export function sameScope(a: RbacScope, b: RbacScope): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'org') return true;
  if (a.kind === 'environment') return b.kind === 'environment' && a.projectId === b.projectId && a.env === b.env;
  return (b as { id: Id }).id === a.id;
}

// ---------------------------------------------------------------------------
// Migration from v1 (ADR 0048 point 12)
// ---------------------------------------------------------------------------

/** The stable id of the org binding a v1 member role becomes. */
export function v1BindingId(memberId: Id): Id {
  return `rbn_v1_${memberId}`;
}

/** Each member's v1 role becomes one org binding (source 'migration'), held in memory. */
export function bindingsFromV1(members: readonly Pick<Member, 'id' | 'role' | 'createdAt'>[]): RoleBinding[] {
  return members.map((m) => ({ id: v1BindingId(m.id), subject: { kind: 'member', id: m.id }, role: m.role, scope: { kind: 'org' }, source: 'migration', grantedBy: 'migration', grantedAt: m.createdAt }));
}

/**
 * Each GitHub `AccessRule { kind: 'org' }` becomes an org group mapping. An owner rule stays an
 * admission rule only: no mapping grants owner.
 */
export function groupMappingsFromV1(rules: readonly Pick<AccessRule, 'kind' | 'name' | 'role'>[]): GroupMapping[] {
  return rules
    .filter((r) => r.kind === 'org' && r.role !== 'owner')
    .map((r) => ({ id: `grm_v1_${r.name.toLowerCase()}`, provider: 'github', group: r.name.toLowerCase(), role: r.role, scope: { kind: 'org' }, createdBy: 'migration', createdAt: 0 }));
}

/** With nothing saved, the whole RBAC state synthesised from v1, in memory. Nothing is written. */
export function snapshotFromV1(v1: {
  members: readonly Pick<Member, 'id' | 'role' | 'createdAt'>[];
  rules?: readonly Pick<AccessRule, 'kind' | 'name' | 'role'>[];
  workspaces?: readonly { id: Id; projectIds: Id[] | null }[];
  rev?: number;
}): RbacSnapshot {
  return {
    rev: v1.rev ?? 0,
    roles: [...BUILT_IN_ROLES],
    bindings: bindingsFromV1(v1.members),
    groupMappings: groupMappingsFromV1(v1.rules ?? []),
    approvalPolicies: [],
    breakglass: [],
    serviceAccounts: [],
    workspaces: (v1.workspaces ?? []).map((w) => ({ id: w.id, projectIds: w.projectIds === null ? null : [...w.projectIds] })),
  };
}

// ---------------------------------------------------------------------------
// The decision (ADR 0048 point 4)
// ---------------------------------------------------------------------------

/**
 * Group names from GitHub, Google and Entra are case-insensitive, so a group subject compares
 * lowercased everywhere (a binding to 'github:Acme' is the group 'github:acme').
 */
export function groupSubjectId(provider: GroupProvider, group: string): string {
  return `${provider}:${group}`.toLowerCase();
}

const subjectKey = (s: RbacSubject): string => `${s.kind}:${s.kind === 'group' ? s.id.toLowerCase() : s.id}`;

/** The subjects whose bindings count for a caller: the person, their teams and groups; or the service account. */
export function subjectsOf(snapshot: Pick<RbacSnapshot, 'serviceAccounts'>, caller: RbacCaller): RbacSubject[] {
  if (caller.kind === 'service-account') {
    const sa = snapshot.serviceAccounts.find((s) => s.id === caller.id);
    return sa && !sa.disabledAt ? [{ kind: 'service-account', id: sa.id }] : [];
  }
  // An agent acts with the grants of the person it acts for (ADR 0041 point 5), never its own.
  const person = caller.kind === 'member' ? caller.id : caller.onBehalfOf;
  if (!person) return [];
  const out: RbacSubject[] = [{ kind: 'member', id: person }];
  for (const t of caller.teams ?? []) out.push({ kind: 'team', id: t });
  for (const g of caller.groups ?? []) out.push({ kind: 'group', id: groupSubjectId(g.provider, g.group) });
  return out;
}

export interface AuthorizeOptions {
  /** The tier the call runs at (the AI gate passes effectiveTier): the granting role must reach it. */
  tier?: CapabilityTier;
  /** Defaults to Date.now(). */
  now?: number;
  /** Display names for ids in the `why` sentence. */
  names?: Readonly<Record<string, string>>;
}

interface Grant {
  binding: RoleBinding;
  role: RbacRole;
}

/** Every binding of these subjects that grants the permission at the resource (and tier). */
function grantsFor(snapshot: RbacSnapshot, subjects: readonly RbacSubject[], p: Permission, resource: RbacResource, tier: CapabilityTier | undefined, now: number): Grant[] {
  const keys = new Set(subjects.map(subjectKey));
  const out: Grant[] = [];
  for (const b of snapshot.bindings) {
    if (!keys.has(subjectKey(b.subject))) continue;
    if (b.expiresAt !== undefined && b.expiresAt <= now) continue;
    if (!p.bindable.includes(b.scope.kind)) continue;
    if (!scopeContains(snapshot, b.scope, resource)) continue;
    const role = roleById(snapshot, b.role);
    if (!role || !roleCapabilities(role).has(p.name)) continue;
    if (tier && tierRank(tier) > tierRank(role.highestTier)) continue;
    out.push({ binding: b, role });
  }
  return out;
}

/**
 * Permissions break-glass never grants: it cannot bind or grant anything, never touches the audit
 * log, trust, sign-in or billing, and cannot start another break-glass (ADR 0048 point 10).
 */
export const BREAKGLASS_NEVER: readonly string[] = [
  'members.invite',
  'members.remove',
  'members.manage',
  'members.owner',
  'roles.manage',
  'bindings.manage',
  'tokens.manage',
  'service-accounts.manage',
  'teams.manage',
  'auth.configure',
  'approval-policies.manage',
  'breakglass.use',
  'scim.provision',
  'trust.manage',
  'audit.configure',
  'billing.manage',
  // Approving is a person's own sign-off (break-glass skips quorums for its holder's own deploy,
  // it never makes them an approver of someone else's), and a join token is a credential.
  'environment.approve',
  'machines.enrol',
];

/** A member's break-glass activations live at `now` (none when the org turned it off). */
export function activeBreakglass(snapshot: Pick<RbacSnapshot, 'breakglass' | 'breakglassEnabled'>, memberId: string, now: number): BreakGlass[] {
  if (snapshot.breakglassEnabled === false) return [];
  return snapshot.breakglass.filter((b) => b.by === memberId && !b.endedAt && b.startedAt <= now && now < b.expiresAt);
}

const refuse = (why: string, sod?: SodRuleId): AuthorizeDecision => ({ allowed: false, via: [], why, ...(sod ? { sod } : {}) });

/**
 * Whether a token's scopes cover a permission where it is used directly. Legacy tokens keep v1's
 * meaning exactly (a route's read, write or admin; 'actions' or the kind for an action). Newer
 * tokens may also name the permission, or a tier at or above it.
 */
export function tokenCovers(token: Pick<TokenGrantV2, 'scopes' | 'legacy'>, p: Permission): boolean {
  const s = token.scopes;
  if (!s.length) return true;
  const c = canonicalPermission(p);
  if (c.tokenScope === 'actions') {
    if (s.includes('actions') || (c.action !== undefined && s.includes(c.action))) return true;
  } else if (c.tokenScope === null || s.includes(c.tokenScope)) return true;
  if (token.legacy) return false;
  return s.includes(p.name) || s.includes(c.name) || s.some((x) => isCapabilityTier(x) && tierRank(x) >= tierRank(p.tier));
}

/** A token acts only for its own subject: the person (or the person an agent acts for), or the service account. */
function tokenBelongsTo(token: Pick<TokenGrantV2, 'subject'>, caller: RbacCaller): boolean {
  if (caller.kind === 'service-account') return token.subject.kind === 'service-account' && token.subject.id === caller.id;
  const person = caller.kind === 'member' ? caller.id : caller.onBehalfOf;
  return token.subject.kind === 'member' && token.subject.id === person;
}

/**
 * May this caller use this permission on this resource? Effective permissions are the union of
 * the roles bound at every scope containing the resource, through the person, their teams and
 * groups (bindings only add), narrowed by the token's role, scopes and resources. Agents and
 * service accounts never approve deploys; an agent service account never reaches critical. An
 * active break-glass grants its role at its scope, except `BREAKGLASS_NEVER`. Separation of
 * duties and approval policies are checked after the grant, by the functions below.
 */
export function authorize(snapshot: RbacSnapshot, caller: RbacCaller, capability: string, resource: RbacResource = { kind: 'org' }, opts: AuthorizeOptions = {}): AuthorizeDecision {
  const now = opts.now ?? Date.now();
  const p = PERMISSION_BY_NAME.get(capability);
  if (!p) return refuse(`${capability} is not a permission Orgus knows.`);
  const c = canonicalPermission(p);
  const where = scopeLabel(resource, opts.names);

  let sa: ServiceAccount | undefined;
  if (caller.kind === 'service-account') {
    sa = snapshot.serviceAccounts.find((s) => s.id === caller.id);
    if (!sa) return refuse(`Service account ${caller.id} does not exist.`);
    if (sa.disabledAt) return refuse(`Service account ${sa.name} is disabled.`);
  }
  if ((caller.kind === 'agent' || caller.kind === 'service-account') && c.name === 'environment.approve') {
    return refuse('Agents and service accounts never approve deploys or count toward a quorum; a person must.');
  }
  if (sa?.agent && maxTier(maxTier(p.tier, c.tier), opts.tier ?? 'read') === 'critical') return refuse(`${p.title} is critical, and an agent service account never reaches critical.`);

  const token = caller.token;
  if (token && !tokenBelongsTo(token, caller)) return refuse('This token belongs to another subject, so it grants nothing here.');
  if (token?.legacy) {
    const r = builtInRole(token.legacy.role);
    if (!roleCapabilities(r).has(c.name)) return refuse(`This token has the ${r.title.toLowerCase()} role, which does not include ${p.name}.`);
    if (opts.tier && tierRank(opts.tier) > tierRank(r.highestTier)) return refuse(`This token has the ${r.title.toLowerCase()} role, which reaches ${r.highestTier}, and this needs ${opts.tier}.`);
  }
  if (token && !tokenCovers(token, p)) {
    return refuse(`This token is limited to ${token.scopes.join(', ')} and ${p.name} needs ${c.tokenScope ?? p.name}.`);
  }
  if (token?.resources?.length && !token.resources.some((r) => scopeContains(snapshot, scopeOfResource(r), resource))) {
    return refuse(`This token is limited to ${token.resources.map((r) => scopeLabel(r, opts.names)).join(', ')}, and ${where} is outside them.`);
  }

  const grants = grantsFor(snapshot, subjectsOf(snapshot, caller), c, resource, opts.tier, now);
  if (grants.length) {
    const g = grants[0]!;
    return { allowed: true, via: grants.map((x) => x.binding.id), why: `${g.role.title} at ${scopeLabel(g.binding.scope, opts.names)} includes ${p.name}.` };
  }

  // Break-glass is a signed-in person's (it needs a sign-in in the last 10 minutes): an API token,
  // which a script may hold, never carries it.
  if (caller.kind === 'member' && !token && !BREAKGLASS_NEVER.includes(c.name)) {
    for (const bg of activeBreakglass(snapshot, caller.id, now)) {
      const role = roleById(snapshot, bg.role);
      if (!role || role.id === 'owner' || !roleCapabilities(role).has(c.name)) continue;
      if (!c.bindable.includes(bg.scope.kind) || !scopeContains(snapshot, bg.scope, resource)) continue;
      if (opts.tier && tierRank(opts.tier) > tierRank(role.highestTier)) continue;
      return { allowed: true, via: [], why: `Break-glass ${bg.id} gives ${role.title} at ${scopeLabel(bg.scope, opts.names)} until it ends.`, breakglassId: bg.id };
    }
  }

  const tierNote = opts.tier ? ` at the ${opts.tier} tier` : '';
  return refuse(`No role you hold at ${where} includes ${p.name}${tierNote}.`);
}

/** The route gate: the permission the route names, at the object it names (`permissionFor`), then `authorize`. */
export function authorizeRoute(snapshot: RbacSnapshot, caller: RbacCaller, req: RouteRequest, plane: RbacPlane = 'hub', opts: AuthorizeOptions = {}): AuthorizeDecision & { capability: string; resource: RbacResource } {
  const rp = permissionFor(req.method, req.path, plane);
  const resource = rp.resolve(req);
  return { ...authorize(snapshot, caller, rp.capability, resource, opts), capability: rp.capability, resource };
}

export interface CapabilityOptions {
  /** effectiveTier(capability, input). */
  tier: CapabilityTier;
  /** A person's sign-off on this exact call (ADR 0041 point 6). Set only in the hub's own process, never from a token. */
  intent?: AiIntent;
  now?: number;
  names?: Readonly<Record<string, string>>;
}

/**
 * The AI gate on grants (ADR 0048 point 4): `authorize` for the capability at the resource and at
 * the call's tier, then the agent rules `decideCapability` has today. Scopes only narrow; agents
 * reach critical only with a person's intent for that capability, and elevated only when a scope
 * names the capability or the tier. Service accounts follow the agent rules.
 */
export function authorizeCapability(snapshot: RbacSnapshot, caller: RbacCaller, capability: string, resource: RbacResource, opts: CapabilityOptions): AuthorizeDecision {
  const p = PERMISSION_BY_NAME.get(capability);
  if (!p) return refuse(`${capability} is not a capability Orgus knows.`);
  const agent = caller.kind !== 'member';
  if (opts.tier === 'critical' && agent && opts.intent?.capability !== p.name) {
    return refuse(`${p.title} is a critical capability, and only a person may use those.`);
  }
  const token = caller.token;
  // Grants (and a legacy token's role) first; the AI gate narrows by its own scope vocabulary below.
  const grantCaller: RbacCaller = token ? { ...caller, token: { ...token, scopes: [] } } : caller;
  const d = authorize(snapshot, grantCaller, capability, resource, { tier: opts.tier, ...(opts.now !== undefined ? { now: opts.now } : {}), ...(opts.names ? { names: opts.names } : {}) });
  if (!d.allowed) return d;
  const raw = token?.scopes ?? [];
  if (raw.length) {
    const scopes = aiScopesOf(agent ? 'agent' : 'human', raw);
    if (!token?.legacy) for (const s of raw) if (PERMISSION_BY_NAME.has(s) && !scopes.includes(s)) scopes.push(s);
    // aiScopesOf turns the broad scopes into tiers (agents capped at operate), as the AI gate does today.
    const ok = scopes.includes(p.name) || scopes.some((s) => isCapabilityTier(s) && tierRank(s) >= tierRank(opts.tier));
    if (!ok) return refuse(`This token's scopes (${scopes.join(', ')}) do not include ${p.name} or the ${opts.tier} tier.`);
    return d;
  }
  if (agent && tierRank(opts.tier) > tierRank(AGENT_DEFAULT_CEILING)) {
    return refuse(`${p.title} needs the ${opts.tier} tier. Agents reach it only when their token's scopes name ${p.name} or '${opts.tier}'.`);
  }
  return d;
}

/**
 * `Actor.role` in v2: the highest v1 role whose whole set (and tier) the caller holds at org
 * scope. Modules not yet moved to `ctx.authorize` read this, so they behave exactly as before and
 * never see scoped grants. A token minted before v2 keeps its own role as a cap (a viewer token of
 * an admin stays viewer); a token for another subject has no role. Null: no org role at all.
 */
export function orgRoleOf(snapshot: RbacSnapshot, caller: RbacCaller, now: number = Date.now()): Role | null {
  if (caller.token && !tokenBelongsTo(caller.token, caller)) return null;
  const tokenRole = caller.token?.legacy?.role;
  const keys = new Set(subjectsOf(snapshot, caller).map(subjectKey));
  const held = new Set<string>();
  let ceiling: CapabilityTier | null = null;
  for (const b of snapshot.bindings) {
    if (b.scope.kind !== 'org' || !keys.has(subjectKey(b.subject))) continue;
    if (b.expiresAt !== undefined && b.expiresAt <= now) continue;
    const role = roleById(snapshot, b.role);
    if (!role) continue;
    for (const c of roleCapabilities(role)) held.add(c);
    ceiling = ceiling ? maxTier(ceiling, role.highestTier) : role.highestTier;
  }
  if (!ceiling) return null;
  for (const r of V1_ROLES_HIGH_FIRST) {
    if (tokenRole && ROLE_RANK[r] > ROLE_RANK[tokenRole]) continue;
    const role = builtInRole(r);
    if (tierRank(ceiling) < tierRank(role.highestTier)) continue;
    let all = true;
    for (const c of roleCapabilities(role)) {
      if (!held.has(c)) {
        all = false;
        break;
      }
    }
    if (all) return r;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Explain: the same reasoning, as sentences
// ---------------------------------------------------------------------------

export interface ExplainContext {
  /** For a member: the teams and groups they are in. */
  teams?: string[];
  groups?: { provider: GroupProvider; group: string }[];
  /** Display names for subject, role, workspace, project and machine ids. */
  names?: Readonly<Record<string, string>>;
  now?: number;
}

/** Which kind of approval using a permission raises under an approval policy, if any. */
const POLICY_KIND: Readonly<Record<string, ApprovalPolicyAppliesTo>> = {
  'environment.deploy': 'deploy',
  'environment.protected': 'deploy',
  'ci.gate.decide': 'ci.gate',
  'landing.submit': 'landing',
  'secrets.lease': 'secrets.lease',
};

/**
 * Why a subject can or cannot use a capability on a resource, as sentences a person can follow:
 * the bindings that grant it, the ones that hold it at a scope that does not reach, the approval
 * policy that still applies, and the rule that refuses an agent.
 */
export function explainAccess(snapshot: RbacSnapshot, req: AccessExplainRequest, ctx: ExplainContext = {}): AccessExplanation {
  const now = ctx.now ?? Date.now();
  const names = ctx.names ?? {};
  const who = names[req.subject.id] ?? req.subject.id;
  const where = scopeLabel(req.resource, names);
  const p = PERMISSION_BY_NAME.get(req.capability);
  if (!p) return { allowed: false, lines: [`${req.capability} is not a permission Orgus knows. Check the name in Settings, Access.`], bindings: [] };
  const c = canonicalPermission(p);
  const lines: string[] = [`${p.title} (${p.name}) on ${where}.`];
  if (c !== p) lines.push(`${p.name} is a setup step; it is allowed exactly when ${c.name} is.`);

  const subjects: RbacSubject[] = [req.subject];
  if (req.subject.kind === 'member') {
    for (const t of ctx.teams ?? []) subjects.push({ kind: 'team', id: t });
    for (const g of ctx.groups ?? []) subjects.push({ kind: 'group', id: groupSubjectId(g.provider, g.group) });
  }
  let sa: ServiceAccount | undefined;
  if (req.subject.kind === 'service-account') {
    sa = snapshot.serviceAccounts.find((s) => s.id === req.subject.id);
    if (!sa || sa.disabledAt) {
      lines.push(sa ? `${sa.name} is disabled, so it holds nothing.` : `Service account ${req.subject.id} does not exist.`);
      return { allowed: false, lines, bindings: [] };
    }
    if (c.name === 'environment.approve') {
      lines.push('Service accounts never approve deploys or count toward a quorum; a person must.');
      return { allowed: false, lines, bindings: [] };
    }
  }

  const grants = grantsFor(snapshot, subjects, c, req.resource, undefined, now);
  for (const g of grants) {
    const through = g.binding.subject.kind === 'member' ? '' : ` through ${g.binding.subject.kind} ${names[g.binding.subject.id] ?? g.binding.subject.id}`;
    const until = g.binding.expiresAt ? `, until ${new Date(g.binding.expiresAt).toISOString()}` : '';
    lines.push(`${who} holds ${g.role.title} at ${scopeLabel(g.binding.scope, names)}${through} (binding ${g.binding.id}, ${g.binding.source}${until}), which includes ${c.name}.`);
  }
  let allowed = grants.length > 0;
  if (!allowed) {
    const keys = new Set(subjects.map(subjectKey));
    const held = snapshot.bindings.filter((b) => keys.has(subjectKey(b.subject)) && (b.expiresAt === undefined || b.expiresAt > now));
    let near = 0;
    for (const b of held) {
      const role = roleById(snapshot, b.role);
      if (!role || !roleCapabilities(role).has(c.name)) continue;
      near++;
      if (!c.bindable.includes(b.scope.kind)) lines.push(`${role.title} at ${scopeLabel(b.scope, names)} includes ${c.name}, but ${c.name} can only be granted at ${c.bindable.join(', ')} scope.`);
      else lines.push(`${role.title} at ${scopeLabel(b.scope, names)} includes ${c.name}, but ${where} is outside that scope.`);
    }
    if (!held.length) lines.push(`${who} holds no role.`);
    else if (!near) lines.push(`${who} holds ${held.map((b) => `${roleById(snapshot, b.role)?.title ?? b.role} at ${scopeLabel(b.scope, names)}`).join('; ')}, and none of these includes ${c.name}.`);
    if (req.subject.kind === 'member' && !BREAKGLASS_NEVER.includes(c.name)) {
      const bg = activeBreakglass(snapshot, req.subject.id, now).find((x) => {
        const role = roleById(snapshot, x.role);
        return !!role && role.id !== 'owner' && roleCapabilities(role).has(c.name) && c.bindable.includes(x.scope.kind) && scopeContains(snapshot, x.scope, req.resource);
      });
      if (bg) {
        allowed = true;
        lines.push(`Break-glass ${bg.id} gives ${roleById(snapshot, bg.role)?.title ?? bg.role} at ${scopeLabel(bg.scope, names)} until ${new Date(bg.expiresAt).toISOString()}; someone else must review it.`);
      }
    }
    if (!allowed) lines.push(`To allow it, an admin binds a role that includes ${c.name} at ${where} or a scope containing it.`);
  }
  if (sa?.agent && p.tier === 'critical') {
    allowed = false;
    lines.push(`${sa.name} is an agent service account, and agents never reach critical.`);
  }

  const kind = POLICY_KIND[c.name];
  let policy: ApprovalPolicy | undefined;
  if (kind && (req.resource.kind === 'environment' || req.resource.kind === 'project')) {
    const target = req.resource.kind === 'environment' ? { projectId: req.resource.projectId, env: req.resource.env } : { projectId: req.resource.id };
    policy = effectiveApprovalPolicy(snapshot.approvalPolicies, target, kind) ?? undefined;
    if (policy) {
      const raise = policy.raiseTo ? `, ${policy.raiseTo.approvers} when ${policy.raiseTo.whenEligibleAtLeast} or more people are eligible` : '';
      const notSelf = policy.forbidSelfApproval || policy.forbidAuthorApproval ? ', not the requester or author' : '';
      lines.push(`It also needs ${policy.minApprovers} approver${policy.minApprovers === 1 ? '' : 's'}${raise}${notSelf} (approval policy ${policy.id} at ${scopeLabel(policy.scope, names)}).`);
    }
  }
  return { allowed, lines, bindings: grants.map((g) => g.binding), ...(policy ? { policy } : {}) };
}

// ---------------------------------------------------------------------------
// Environment approval policies and quorum (ADR 0048 point 8)
// ---------------------------------------------------------------------------

const POLICY_RANK: Record<ApprovalPolicyScope['kind'], number> = { org: 0, project: 1, environment: 2 };

/** The policy for a target: the narrowest scope wins (environment over project over org). */
export function effectiveApprovalPolicy(policies: readonly ApprovalPolicy[], target: { projectId?: Id; env?: string }, appliesTo: ApprovalPolicyAppliesTo): ApprovalPolicy | null {
  let best: ApprovalPolicy | null = null;
  for (const p of policies) {
    if (!p.appliesTo.includes(appliesTo)) continue;
    const s = p.scope;
    const hit = s.kind === 'org' || (s.kind === 'project' && s.id === target.projectId) || (s.kind === 'environment' && s.projectId === target.projectId && s.env === target.env);
    if (!hit) continue;
    if (!best || POLICY_RANK[s.kind] > POLICY_RANK[best.scope.kind]) best = p;
  }
  return best;
}

/** How many approvers a policy asks for, given how many people are eligible. */
export function requiredApprovers(policy: Pick<ApprovalPolicy, 'minApprovers' | 'raiseTo'>, eligibleCount: number): number {
  let n = Math.min(Math.max(1, Math.floor(policy.minApprovers)), APPROVAL_POLICY_MAX_APPROVERS);
  if (policy.raiseTo && eligibleCount >= policy.raiseTo.whenEligibleAtLeast) n = Math.max(n, Math.min(policy.raiseTo.approvers, APPROVAL_POLICY_MAX_APPROVERS));
  return n;
}

/** One approval as recorded on the request. */
export interface QuorumVote {
  /** The actor id that approved (a login, agent:<name>, a service account). */
  by: string;
  at: Ts;
  /** The typed reason for a solo owner approving their own request. */
  soloOwnerReason?: string;
}

export interface QuorumContext {
  /** The member who raised the request (null when an agent or a service account raised it). */
  requester: string | null;
  /** Members who authored what is being approved (submitted the landing, own the mission, pushed the commits). */
  authors?: readonly string[];
  /** The member behind an actor id: linked sign-ins of one person resolve to one member; null for agents and service accounts. */
  memberOf: (by: string) => string | null;
  /** Members eligible to approve under the policy (`isEligibleApprover`). */
  eligible: readonly string[];
  /** Members who are owners (the solo-owner path). */
  owners?: readonly string[];
  /** A member's team ids, for `distinctTeams`. */
  teamsOf?: (memberId: string) => readonly string[];
  now: number;
}

export type QuorumRefusal = SodRuleId | 'agent' | 'duplicate' | 'not-eligible';

export interface QuorumTally {
  met: boolean;
  needed: number;
  /** Members counted, once each. */
  counted: string[];
  refused: { by: string; rule: QuorumRefusal; why: string }[];
  /** Set when a solo owner's own approval was counted (recorded as sod: 'solo-owner'). */
  soloOwner?: string;
  /** Why the quorum is not met yet. */
  why?: string;
}

/**
 * Count approvals under a policy. Approvals count people, not identities: linked sign-ins of one
 * member count once. Agents and service accounts never count. The requester and authors are
 * refused where the policy says so (sod/self-approval), and so is whoever changed the policy in
 * the last 24 hours (sod/policy-and-approve). A one-person org still works: when nobody else is
 * eligible, an owner may approve their own request with a typed reason.
 */
export function tallyQuorum(policy: ApprovalPolicy, votes: readonly QuorumVote[], ctx: QuorumContext): QuorumTally {
  const eligible = new Set(ctx.eligible);
  const needed = requiredApprovers(policy, eligible.size);
  const counted: string[] = [];
  const refused: QuorumTally['refused'] = [];
  const authors = new Set(ctx.authors ?? []);
  const others = [...eligible].filter((m) => m !== ctx.requester && !authors.has(m));
  let soloOwner: string | undefined;
  for (const v of votes) {
    const member = ctx.memberOf(v.by);
    if (!member) {
      refused.push({ by: v.by, rule: 'agent', why: 'Agents and service accounts never count toward a quorum.' });
      continue;
    }
    if (counted.includes(member)) {
      refused.push({ by: v.by, rule: 'duplicate', why: 'This person already approved; linked sign-ins count once.' });
      continue;
    }
    const self = (policy.forbidSelfApproval && member === ctx.requester) || (policy.forbidAuthorApproval && authors.has(member));
    if (self) {
      const solo = others.length === 0 && (ctx.owners ?? []).includes(member) && !!v.soloOwnerReason?.trim();
      if (!solo) {
        refused.push({ by: v.by, rule: 'sod/self-approval', why: member === ctx.requester ? 'You raised this request, and the policy asks for someone else.' : 'You authored this change, and the policy asks for someone else.' });
        continue;
      }
      soloOwner = member;
    } else if (!eligible.has(member)) {
      refused.push({ by: v.by, rule: 'not-eligible', why: 'This person is not eligible to approve under the policy.' });
      continue;
    }
    if (policy.updatedBy === member && ctx.now - policy.updatedAt < SOD_POLICY_COOLDOWN_MS) {
      refused.push({ by: v.by, rule: 'sod/policy-and-approve', why: 'You changed this approval policy in the last 24 hours.' });
      continue;
    }
    counted.push(member);
  }
  const need = soloOwner ? 1 : needed;
  let met = counted.length >= need;
  let why: string | undefined;
  if (met && policy.distinctTeams && !soloOwner && need > 1) {
    const teams = new Set<string>();
    for (const m of counted) for (const t of ctx.teamsOf?.(m) ?? []) teams.add(t);
    if (teams.size < 2) {
      met = false;
      why = 'The approvers must come from at least two teams.';
    }
  }
  if (!met && !why) why = `${counted.length} of ${need} approvals so far.`;
  return { met, needed: need, counted, refused, ...(soloOwner ? { soloOwner } : {}), ...(!met && why ? { why } : {}) };
}

/**
 * Whether a caller may approve under a policy at a resource: a person holding environment.approve
 * there, or a member of the policy's teams. Agents and service accounts never.
 */
export function isEligibleApprover(snapshot: RbacSnapshot, policy: Pick<ApprovalPolicy, 'eligible'>, caller: RbacCaller, resource: RbacResource, now: number = Date.now()): boolean {
  if (caller.kind !== 'member') return false;
  if (policy.eligible === 'capability') {
    const person: RbacCaller = { kind: 'member', id: caller.id, ...(caller.teams ? { teams: caller.teams } : {}), ...(caller.groups ? { groups: caller.groups } : {}) };
    return authorize(snapshot, person, 'environment.approve', resource, { now }).allowed;
  }
  const teams = new Set(policy.eligible.teams);
  return (caller.teams ?? []).some((t) => teams.has(t));
}

type PolicyShape = Pick<ApprovalPolicy, 'appliesTo' | 'minApprovers' | 'raiseTo' | 'eligible' | 'forbidSelfApproval' | 'forbidAuthorApproval' | 'distinctTeams'>;

/** Whether a change loosens a policy: fewer approvers, self-approval allowed, fewer kinds, wider eligibility, or removal (null). */
export function loosensPolicy(before: PolicyShape, after: PolicyShape | null): boolean {
  if (!after) return true;
  if (after.minApprovers < before.minApprovers) return true;
  if (before.raiseTo && (!after.raiseTo || after.raiseTo.approvers < before.raiseTo.approvers || after.raiseTo.whenEligibleAtLeast > before.raiseTo.whenEligibleAtLeast)) return true;
  if ((before.forbidSelfApproval && !after.forbidSelfApproval) || (before.forbidAuthorApproval && !after.forbidAuthorApproval)) return true;
  if (before.distinctTeams && !after.distinctTeams) return true;
  if (before.appliesTo.some((k) => !after.appliesTo.includes(k))) return true;
  if (before.eligible !== 'capability') {
    if (after.eligible === 'capability') return true;
    const was = new Set(before.eligible.teams);
    if (after.eligible.teams.some((t) => !was.has(t))) return true;
  }
  return false;
}

const POLICY_KINDS: readonly ApprovalPolicyAppliesTo[] = ['deploy', 'promote', 'rollback', 'landing', 'ci.gate', 'secrets.lease', 'tool'];

/** Validate an approval policy request: null when it is fine, else what is wrong. */
export function approvalPolicyError(req: ApprovalPolicyRequest): string | null {
  if (!req || typeof req !== 'object') return 'The policy must be an object.';
  const s = req.scope as unknown as { kind?: unknown } | undefined;
  if (!s || (s.kind !== 'org' && s.kind !== 'project' && s.kind !== 'environment') || !isRbacScope(s)) return 'The scope must be the org, a project or an environment.';
  if (!Array.isArray(req.appliesTo) || !req.appliesTo.length) return 'Say what the policy applies to: deploy, promote, rollback, landing, ci.gate, secrets.lease or tool.';
  const bad = req.appliesTo.find((k) => !POLICY_KINDS.includes(k));
  if (bad !== undefined) return `${String(bad)} is not something a policy can apply to.`;
  if (!Number.isInteger(req.minApprovers) || req.minApprovers < 1 || req.minApprovers > APPROVAL_POLICY_MAX_APPROVERS) return `Approvers must be a whole number from 1 to ${APPROVAL_POLICY_MAX_APPROVERS}.`;
  if (req.raiseTo) {
    const r = req.raiseTo;
    if (!Number.isInteger(r.approvers) || r.approvers <= req.minApprovers || r.approvers > APPROVAL_POLICY_MAX_APPROVERS) return `A raised count must be more than ${req.minApprovers} and at most ${APPROVAL_POLICY_MAX_APPROVERS}.`;
    if (!Number.isInteger(r.whenEligibleAtLeast) || r.whenEligibleAtLeast < r.approvers) return 'Raise the count only when at least that many people are eligible.';
  }
  if (req.eligible !== 'capability' && (!req.eligible || !Array.isArray(req.eligible.teams) || !req.eligible.teams.length || !req.eligible.teams.every((t) => typeof t === 'string' && t.length > 0 && t.length <= 200))) return "Eligible approvers are 'capability' or a list of teams.";
  // A missing flag must never read as false: that would allow self-approval without anyone saying so.
  if (typeof req.forbidSelfApproval !== 'boolean' || typeof req.forbidAuthorApproval !== 'boolean') return 'Say whether the requester and the author may approve: forbidSelfApproval and forbidAuthorApproval are true or false.';
  if (req.distinctTeams !== undefined && typeof req.distinctTeams !== 'boolean') return 'distinctTeams is true or false.';
  if (!Number.isInteger(req.expiresAfterMinutes) || req.expiresAfterMinutes < 1 || req.expiresAfterMinutes > 30 * 24 * 60) return 'A request expires after 1 minute to 30 days.';
  return null;
}

// ---------------------------------------------------------------------------
// Separation of duties (ADR 0048 point 9)
// ---------------------------------------------------------------------------

export interface SodRefusal {
  rule: SodRuleId;
  why: string;
}

/** Who makes an access change, as the separation-of-duties rules see them. */
export interface SodActor {
  memberId: string;
  teams?: readonly string[];
  groups?: readonly { provider: GroupProvider; group: string }[];
  /** Service accounts this person owns, directly or through a team. */
  ownsServiceAccounts?: readonly string[];
}

/**
 * sod/self-grant: a person cannot create or widen a binding, role, mapping or token for
 * themselves, a team or group they are in, or a service account they own.
 */
export function checkSelfGrant(actor: SodActor, target: RbacSubject): SodRefusal | null {
  const mine =
    (target.kind === 'member' && target.id === actor.memberId) ||
    (target.kind === 'team' && (actor.teams ?? []).includes(target.id)) ||
    (target.kind === 'group' && (actor.groups ?? []).some((g) => groupSubjectId(g.provider, g.group) === target.id.toLowerCase())) ||
    (target.kind === 'service-account' && (actor.ownsServiceAccounts ?? []).includes(target.id));
  if (!mine) return null;
  return { rule: 'sod/self-grant', why: 'You cannot grant or widen access for yourself, a team or group you are in, or a service account you own. Ask another admin.' };
}

export type TwoPersonChange = { kind: 'binding'; role: RbacRole; scope: RbacScope } | { kind: 'mapping'; role: RbacRole } | { kind: 'policy'; production: boolean; loosens: boolean };

/** Whether a change falls under sod/two-person-admin (before counting admins). */
export function needsSecondAdmin(change: TwoPersonChange): boolean {
  if (change.kind === 'binding') return change.scope.kind === 'org' && isAdminLevelRole(change.role);
  if (change.kind === 'mapping') return isAdminLevelRole(change.role);
  return change.production && change.loosens;
}

/**
 * sod/two-person-admin: granting admin or owner at org scope, creating an admin-level mapping, or
 * loosening a production approval policy needs a second admin's approval once the org has two or
 * more admins (owners count as admins).
 */
export function checkTwoPersonAdmin(change: TwoPersonChange, ctx: { actor: string; adminCount: number; secondApprover?: string | null }): SodRefusal | null {
  if (!needsSecondAdmin(change) || ctx.adminCount < 2) return null;
  if (ctx.secondApprover && ctx.secondApprover !== ctx.actor) return null;
  return { rule: 'sod/two-person-admin', why: 'This change needs a second admin to approve it, because the org has two or more admins.' };
}

/** sod/breakglass: a break-glass activation is reviewed by someone other than the person who used it. */
export function checkBreakglassReview(bg: Pick<BreakGlass, 'by'>, reviewer: string): SodRefusal | null {
  return bg.by === reviewer ? { rule: 'sod/breakglass', why: 'Someone other than the person who used break-glass must review it.' } : null;
}

/** sod/policy-and-approve on its own: whoever changed the policy cannot approve under it for 24 hours. */
export function checkPolicyCooldown(policy: Pick<ApprovalPolicy, 'updatedBy' | 'updatedAt'>, approver: string, now: number): SodRefusal | null {
  return policy.updatedBy === approver && now - policy.updatedAt < SOD_POLICY_COOLDOWN_MS
    ? { rule: 'sod/policy-and-approve', why: 'You changed this approval policy in the last 24 hours, so someone else approves under it until then.' }
    : null;
}

// ---------------------------------------------------------------------------
// Roles, bindings and mappings: validation (pure; the hub store applies them)
// ---------------------------------------------------------------------------

/**
 * Validate a custom role; the capability list comes back deduplicated, with its highest tier.
 * No custom role grants the owner role (`members.owner`), and only an owner may name the other
 * owner-level permissions (billing, break-glass, SCIM), so an admin cannot compose a role above
 * their own and hand it to someone else.
 */
export function validateRoleRequest(req: RoleRequest, opts: { creatorIsOwner?: boolean } = {}): { ok: true; capabilities: string[]; highestTier: CapabilityTier } | { ok: false; why: string } {
  if (!req || typeof req !== 'object') return { ok: false, why: 'The role must be an object.' };
  const title = typeof req.title === 'string' ? req.title.trim() : '';
  if (!title || title.length > 60) return { ok: false, why: 'A role needs a title of 1 to 60 characters.' };
  if (BUILT_IN_ROLE_NAMES.some((n) => BUILT_IN_ROLE_TITLE[n].toLowerCase() === title.toLowerCase())) return { ok: false, why: `${title} is a built-in role; pick another title.` };
  if (req.description !== undefined && (typeof req.description !== 'string' || req.description.length > 500)) return { ok: false, why: 'The description is at most 500 characters.' };
  if (!Array.isArray(req.capabilities) || !req.capabilities.length) return { ok: false, why: 'A role needs at least one capability.' };
  const out: string[] = [];
  for (const c of req.capabilities) {
    if (typeof c !== 'string') return { ok: false, why: 'Capabilities are names from the catalogue.' };
    if (isCapabilityTier(c)) {
      out.push(c);
      continue;
    }
    const p = PERMISSION_BY_NAME.get(c);
    if (!p) return { ok: false, why: `${c} is not in the permission catalogue.` };
    const canon = canonicalPermission(p);
    if (!canon.bindable.length) return { ok: false, why: `${c} cannot be granted at any scope.` };
    if (canon.name === 'members.owner') return { ok: false, why: 'Only the built-in owner role grants the owner role.' };
    if (OWNER_ONLY.has(canon.name) && !opts.creatorIsOwner) return { ok: false, why: `${c} is owner-level, so only an owner can put it in a role.` };
    out.push(canon.name);
  }
  const capabilities = [...new Set(out)];
  return { ok: true, capabilities, highestTier: highestTierOf(capabilities) };
}

/** A custom role from a request; a change keeps the id and bumps the version (versioned like policy). */
export function buildCustomRole(req: RoleRequest, by: string, now: number, previous?: RbacRole, opts: { creatorIsOwner?: boolean } = {}): RbacRole | { error: string } {
  if (previous?.builtIn) return { error: 'Built-in roles cannot be changed.' };
  const v = validateRoleRequest(req, opts);
  if (!v.ok) return { error: v.why };
  return {
    id: previous?.id ?? newRoleId(now),
    title: req.title.trim(),
    description: (req.description ?? '').trim(),
    capabilities: v.capabilities,
    builtIn: false,
    highestTier: v.highestTier,
    version: (previous?.version ?? 0) + 1,
    updatedBy: by,
    updatedAt: now,
  };
}

/**
 * Validate a binding: who and where are well-formed and exist, the owner role only to a person at
 * the org, a group bound as a group mapping would be (never owner, admin-level only by an owner),
 * no critical role for an agent service account, and a role that grants something at that scope
 * (a binding that grants nothing is refused). Null when it may be created.
 */
export function bindingError(snapshot: RbacSnapshot, req: RoleBindingRequest, now: number, opts: { creatorIsOwner?: boolean } = {}): string | null {
  if (!req || typeof req !== 'object') return 'The binding must be an object.';
  const s = req.subject as RbacSubject | undefined;
  if (!s || !['member', 'team', 'group', 'service-account'].includes(s.kind) || typeof s.id !== 'string' || !s.id || s.id.length > 300) return 'Name who gets the role: a member, team, group or service account.';
  if (s.kind === 'group' && !/^(github|google|microsoft):./i.test(s.id)) return 'Name a group as provider:group, where the provider is github, google or microsoft.';
  if (!isRbacScope(req.scope)) return 'The scope must be the org, a workspace, a project, an environment or a computer.';
  if (typeof req.role !== 'string') return 'Name the role.';
  const role = roleById(snapshot, req.role);
  if (!role) return `${req.role} is not a role.`;
  const scope = req.scope;
  if (role.id === 'owner' && scope.kind !== 'org') return 'The owner role is granted at the org only.';
  if (role.id === 'owner' && s.kind !== 'member') return 'The owner role is granted to a person only, never to a team, group or service account.';
  if (s.kind === 'group' && isAdminLevelRole(role) && !opts.creatorIsOwner) return 'Only an owner can bind a group to an admin-level role.';
  if (scope.kind === 'workspace' && !snapshot.workspaces.some((w) => w.id === scope.id)) return `Workspace ${scope.id} does not exist.`;
  const grantsSomething = [...roleCapabilities(role)].some((c) => PERMISSION_BY_NAME.get(c)?.bindable.includes(scope.kind));
  if (!grantsSomething) return `${role.title} grants nothing at ${scope.kind} scope.`;
  if (req.expiresAt !== undefined && (!Number.isFinite(req.expiresAt) || req.expiresAt <= now)) return 'An expiry must be in the future.';
  if (s.kind === 'service-account') {
    const sa = snapshot.serviceAccounts.find((x) => x.id === s.id);
    if (!sa || sa.disabledAt) return `Service account ${s.id} does not exist or is disabled.`;
    if (sa.agent && role.highestTier === 'critical') return `${sa.name} is an agent service account, and agents never reach critical.`;
  }
  return null;
}

/** Validate a group mapping: never owner; an admin-level mapping needs an owner to create it. */
export function groupMappingError(snapshot: RbacSnapshot, m: Pick<GroupMapping, 'provider' | 'group' | 'role' | 'scope'>, creatorIsOwner: boolean): string | null {
  if (!['github', 'google', 'microsoft'].includes(m.provider)) return 'Groups come from GitHub, Google or Microsoft.';
  if (typeof m.group !== 'string' || !m.group.trim() || m.group.length > 300) return 'Name the group.';
  if (!isRbacScope(m.scope)) return 'The scope must be the org, a workspace, a project, an environment or a computer.';
  const role = roleById(snapshot, m.role);
  if (!role) return `${m.role} is not a role.`;
  if (role.id === 'owner') return 'No group mapping grants the owner role.';
  if (isAdminLevelRole(role) && !creatorIsOwner) return 'Only an owner can map a group to an admin-level role.';
  return null;
}

/**
 * Reconcile a member's IdP-group bindings at sign-in (or on a SCIM push): add a binding for every
 * mapping whose group they are in, remove 'idp' bindings whose mapping no longer matches. Manual
 * bindings are never touched. A group never admits anyone: call this only for admitted members.
 */
export function reconcileGroupBindings(snapshot: RbacSnapshot, memberId: Id, groups: readonly { provider: GroupProvider; group: string }[], now: number): { add: RoleBinding[]; remove: Id[] } {
  const inGroup = (m: GroupMapping): boolean => groups.some((g) => g.provider === m.provider && g.group.toLowerCase() === m.group.toLowerCase());
  const wanted = snapshot.groupMappings.filter((m) => m.role !== 'owner' && inGroup(m));
  const mine = snapshot.bindings.filter((b) => b.source === 'idp' && b.subject.kind === 'member' && b.subject.id === memberId);
  const reasonOf = (m: GroupMapping): string => `group mapping ${m.id}`;
  const matches = (b: RoleBinding, m: GroupMapping): boolean => b.reason === reasonOf(m) && b.role === m.role && sameScope(b.scope, m.scope);
  const remove = mine.filter((b) => !wanted.some((m) => matches(b, m))).map((b) => b.id);
  const add: RoleBinding[] = wanted
    .filter((m) => !mine.some((b) => matches(b, m)))
    .map((m) => ({ id: newRoleBindingId(now), subject: { kind: 'member', id: memberId }, role: m.role, scope: m.scope, source: 'idp', grantedBy: `${m.provider}:${m.group}`, grantedAt: now, reason: reasonOf(m) }));
  return { add, remove };
}

// ---------------------------------------------------------------------------
// Break-glass (ADR 0048 point 10)
// ---------------------------------------------------------------------------

/**
 * Validate a break-glass activation: on for the org, a person, a reason, a sign-in in the last 10
 * minutes, breakglass.use held at the scope, a role that exists (never owner), at most four hours.
 * Null when it may start.
 */
export function breakglassError(snapshot: RbacSnapshot, caller: RbacCaller, req: BreakGlassRequest, ctx: { lastSignInAt: Ts | null; now: number }): string | null {
  if (snapshot.breakglassEnabled === false) return 'Break-glass is turned off for this org.';
  if (caller.kind !== 'member') return 'Only a person can use break-glass.';
  if (!req || typeof req.reason !== 'string' || req.reason.trim().length < 10) return 'Say why, in at least 10 characters.';
  if (!isRbacScope(req.scope)) return 'The scope must be the org, a workspace, a project, an environment or a computer.';
  const role = roleById(snapshot, req.role);
  if (!role) return `${req.role} is not a role.`;
  if (role.id === 'owner') return 'Break-glass never grants the owner role.';
  const minutes = req.minutes ?? BREAKGLASS_DEFAULT_MINUTES;
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > BREAKGLASS_MAX_MINUTES) return `Break-glass lasts 1 to ${BREAKGLASS_MAX_MINUTES} minutes.`;
  if (ctx.lastSignInAt === null || ctx.now - ctx.lastSignInAt > BREAKGLASS_RECENT_SIGNIN_MS) return 'Sign in again first: break-glass needs a sign-in in the last 10 minutes.';
  const person: RbacCaller = { kind: 'member', id: caller.id, ...(caller.teams ? { teams: caller.teams } : {}), ...(caller.groups ? { groups: caller.groups } : {}) };
  if (!authorize(snapshot, person, 'breakglass.use', req.scope, { now: ctx.now }).allowed) return `You do not hold breakglass.use at ${scopeLabel(req.scope)}.`;
  return null;
}

/** A break-glass record for a validated request. */
export function newBreakglass(caller: { id: string }, req: BreakGlassRequest, now: number): BreakGlass {
  const minutes = req.minutes ?? BREAKGLASS_DEFAULT_MINUTES;
  return { id: newBreakGlassId(now), by: caller.id, role: req.role, scope: req.scope, reason: req.reason.trim(), startedAt: now, expiresAt: now + minutes * 60_000 };
}

// ---------------------------------------------------------------------------
// Access review (ADR 0048 point 11)
// ---------------------------------------------------------------------------

/**
 * Who holds what, at which scope, through which binding, and when each was last used; grants
 * unused for 90 days are stale. Expired bindings are left out.
 */
export function accessReview(snapshot: RbacSnapshot, ctx: { now: number; names?: Readonly<Record<string, string>>; lastUsed?: (bindingId: Id) => Ts | null }): AccessReviewRow[] {
  const staleMs = ACCESS_REVIEW_STALE_DAYS * 24 * 60 * 60_000;
  return snapshot.bindings
    .filter((b) => b.expiresAt === undefined || b.expiresAt > ctx.now)
    .map((b) => {
      const lastUsedAt = ctx.lastUsed?.(b.id) ?? null;
      const since = lastUsedAt ?? b.grantedAt;
      return { subject: b.subject, subjectName: ctx.names?.[b.subject.id] ?? b.subject.id, role: b.role, scope: b.scope, bindingId: b.id, source: b.source, lastUsedAt, stale: ctx.now - since >= staleMs };
    });
}
